// src/pages/Invoices/Report/SalesByDayReport.jsx

import React, { useMemo, useState, useEffect, useCallback } from "react";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { useTranslation } from "react-i18next";
import { Typography } from "@mui/material";
import CalendarViewDayIcon from "@mui/icons-material/CalendarViewDay";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import InsightsIcon from "@mui/icons-material/Insights";
import EmojiEventsOutlinedIcon from "@mui/icons-material/EmojiEventsOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import "dayjs/locale/ar";
import "dayjs/locale/en";

import axios from "../../../axios";
import {
  ReportPage, ReportFilters, ReportSelect, ReportTableCard,
  useReportDateRange, useAllowedBranches, reportGridSx, fmtMoney, fmtNumber,
} from "../../../components/Reports/ReportKit";

export default function SalesByDayReport() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language === "ar" ? "ar" : "en";
  const companyId = parseInt(localStorage.getItem("companyId"), 10);

  const range = useReportDateRange("today");
  const { branchOptions, defaultBranch, ready, branchName } = useAllowedBranches(companyId);
  const [branchId, setBranchId] = useState("");

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  // فرع المستخدم الافتراضي بعد تحميل الفروع
  useEffect(() => { if (ready) setBranchId(defaultBranch); }, [ready, defaultBranch]);

  const fetchReport = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const res = await axios.get("/Invoice/SalesByDay", {
        params: {
          companyId,
          branchId: branchId ? Number(branchId) : 0,
          dateFrom: `${range.dateFrom}T00:00:00`,
          dateTo: `${range.dateTo}T23:59:59`,
          lang,
        },
      });
      const data = Array.isArray(res.data) ? res.data : [];
      setRows(data.map((r, i) => ({
        id: i + 1,
        date: r.date ?? r.Date ?? null,
        dayName: r.dayName ?? r.DayName ?? "",
        netSales: Number(r.netSales ?? r.NetSales ?? 0),
      })));
    } catch {
      toast.error("❌ فشل جلب التقرير");
    } finally {
      setLoading(false);
    }
  }, [companyId, branchId, range.dateFrom, range.dateTo, lang]);

  // تحديث تلقائي عند تغيير الفترة أو الفرع
  useEffect(() => { if (ready) fetchReport(); }, [ready, fetchReport]);

  const stats = useMemo(() => {
    const total = rows.reduce((s, r) => s + r.netSales, 0);
    const best = rows.reduce((b, r) => (!b || r.netSales > b.netSales ? r : b), null);
    return { total, days: rows.length, avg: rows.length ? total / rows.length : 0, best };
  }, [rows]);

  const columns = useMemo(() => [
    {
      field: "date",
      headerName: t("ledgerReport.date", "التاريخ"),
      flex: 1, minWidth: 130, headerAlign: "center", align: "center",
      renderCell: (p) => (
        <Typography sx={{ direction: "ltr", fontWeight: 600, fontSize: 14 }}>
          {p.value ? dayjs(p.value).format("YYYY-MM-DD") : ""}
        </Typography>
      ),
    },
    {
      field: "dayName",
      headerName: t("ledgerReport.dayName", "اليوم"),
      flex: 1, minWidth: 110, headerAlign: "center", align: "center",
    },
    {
      field: "netSales",
      headerName: t("invoice.summary.net", "صافي المبيعات"),
      flex: 1, minWidth: 140, type: "number", headerAlign: "center", align: "center",
      renderCell: (p) => (
        <Typography sx={{
          direction: "ltr", fontWeight: 800, fontSize: 14,
          color: stats.best && p.row.id === stats.best.id ? "#059669" : "#0f172a",
        }}>
          {fmtMoney(p.value)}
        </Typography>
      ),
    },
  ], [t, stats.best]);

  const resetFilters = () => {
    range.reset();
    setBranchId(defaultBranch);
  };

  const bName = branchName(branchId);

  return (
    <ReportPage
      title={t("salesMenu.dailySalesReport", "تقرير المبيعات اليومية")}
      icon={<CalendarViewDayIcon />}
      period={range}
      badges={bName ? [{ label: bName, icon: <StorefrontOutlinedIcon /> }] : []}
      actions={[{ label: "تحديث", icon: <RefreshIcon />, onClick: fetchReport, disabled: loading, primary: true }]}
      stats={[
        { label: "عدد الأيام", value: fmtNumber(stats.days), icon: <EventAvailableIcon />, color: "primary" },
        { label: "متوسط اليوم", value: fmtMoney(stats.avg), icon: <InsightsIcon />, color: "warning" },
        {
          label: stats.best ? `أعلى يوم · ${dayjs(stats.best.date).format("DD/MM")}` : "أعلى يوم",
          value: fmtMoney(stats.best?.netSales), icon: <EmojiEventsOutlinedIcon />, color: "info",
        },
        { label: "إجمالي المبيعات", value: fmtMoney(stats.total), icon: <PaidOutlinedIcon />, color: "success", highlight: true },
      ]}
      filters={
        <ReportFilters
          range={range}
          onApply={fetchReport}
          onReset={resetFilters}
          loading={loading}
          activeCount={branchId ? 1 : 0}
        >
          <ReportSelect label={t("ledgerReport.branch", "الفرع")} value={branchId} onChange={setBranchId} options={branchOptions} />
        </ReportFilters>
      }
    >
      <ReportTableCard title="المبيعات حسب اليوم" count={fmtNumber(rows.length)}>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          slots={{ toolbar: GridToolbar }}
          initialState={{ pagination: { paginationModel: { pageSize: 31 } } }}
          pageSizeOptions={[31, 62, 100]}
          disableRowSelectionOnClick
          localeText={{
            noRowsLabel: "لا توجد مبيعات في هذه الفترة",
            toolbarColumns: "الأعمدة",
            toolbarFilters: "الفلاتر",
            toolbarDensity: "الكثافة",
            toolbarExport: "تصدير",
          }}
          sx={reportGridSx}
        />
      </ReportTableCard>
    </ReportPage>
  );
}
